import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  ClipboardCheck,
  Clock,
  MapPin,
  Lock,
  Settings2,
  CheckCircle2,
  MessageSquarePlus,
  ArrowUpFromLine,
} from "lucide-react";
import { toast } from "sonner";
import ChecklistTemplateEditor from "./ChecklistTemplateEditor";

const SHIFTS = [
  { id: "mattino", label: "Mattino", time: "06:30–15:50" },
  { id: "pomeriggio", label: "Pomeriggio", time: "13:00–22:20" },
  { id: "notte", label: "Notte", time: "22:10–06:50" },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const ChecklistPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [shift, setShift] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [openNotes, setOpenNotes] = useState({});

  const canCheck = meta?.can_check && instance?.status !== "closed";
  const isSuperAdmin = meta?.is_super_admin;

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const res = await axios.get(`${API}/checklists/meta`);
        setMeta(res.data);
        setShift(res.data.current_shift || "mattino");
      } catch {
        toast.error("Errore nel caricamento");
        navigate("/");
      }
    };
    loadMeta();
  }, [navigate]);

  const loadInstance = useCallback(async () => {
    if (!shift) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/checklists/instance`, { params: { shift, date } });
      setInstance(res.data);
    } catch {
      toast.error("Errore nel caricamento della check list");
    } finally {
      setLoading(false);
    }
  }, [shift, date]);

  useEffect(() => { loadInstance(); }, [loadInstance]);

  const toggleItem = async (item) => {
    if (!canCheck) return;
    try {
      const res = await axios.patch(
        `${API}/checklists/instance/${instance.id}/item/${item.id}`,
        { checked: !item.checked }
      );
      setInstance(res.data);
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const saveNote = async (item, note) => {
    try {
      const res = await axios.patch(
        `${API}/checklists/instance/${instance.id}/item/${item.id}`,
        { note }
      );
      setInstance(res.data);
    } catch {
      toast.error("Errore nel salvataggio della nota");
    }
  };

  const handleCloseShift = async () => {
    // Every unchecked OWN (non-carried) item must have a motivation note
    const missing = items.filter((i) => !i.checked && !i.carried && !((i.note || "").trim()));
    if (missing.length > 0) {
      toast.error(`Inserisci una motivazione per le ${missing.length} voci non completate prima di chiudere`);
      setOpenNotes((p) => {
        const next = { ...p };
        missing.forEach((m) => { next[m.id] = true; });
        return next;
      });
      const el = document.querySelector(`[data-testid="checklist-item-${missing[0].id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!window.confirm("Chiudere il turno? Le voci non spuntate verranno riportate al turno successivo.")) return;
    try {
      const res = await axios.post(`${API}/checklists/instance/${instance.id}/close`);
      setInstance(res.data);
      toast.success("Turno chiuso — passaggio consegne effettuato");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : "Errore nella chiusura del turno");
    }
  };

  const items = instance?.items || [];
  const carried = items.filter((i) => i.carried);
  const normal = items.filter((i) => !i.carried);
  const doneCount = items.filter((i) => i.checked).length;
  const totalCount = items.length;
  const progress = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  // group normal items by section preserving order
  const sections = [];
  const sectionMap = {};
  normal.forEach((it) => {
    if (!sectionMap[it.section_title]) {
      sectionMap[it.section_title] = { title: it.section_title, items: [] };
      sections.push(sectionMap[it.section_title]);
    }
    sectionMap[it.section_title].items.push(it);
  });

  const ItemRow = ({ item, carried: isCarried }) => {
    const noteOpen = openNotes[item.id] || (item.note && !isCarried);
    return (
      <div
        className={`rounded-xl border p-3 transition-all ${
          item.checked
            ? "border-emerald-200 bg-emerald-50/60"
            : isCarried
            ? "border-amber-300 bg-amber-50/70"
            : "border-stone-200 bg-white"
        }`}
        data-testid={`checklist-item-${item.id}`}
      >
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleItem(item)}
            disabled={!canCheck}
            className={`mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              item.checked
                ? "bg-emerald-500 border-emerald-500 text-white"
                : "border-stone-300 bg-white hover:border-amber-400"
            } ${!canCheck ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
            data-testid={`checkbox-${item.id}`}
            aria-label="spunta voce"
          >
            {item.checked && <CheckCircle2 className="w-4 h-4" />}
          </button>

          <div className="flex-1 min-w-0">
            <p className={`text-sm leading-snug ${item.checked ? "text-emerald-800 line-through decoration-emerald-400" : "text-stone-800"}`}>
              {item.text}
            </p>

            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              {isCarried && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                  <ArrowUpFromLine className="w-3 h-3" />
                  Da {SHIFTS.find((s) => s.id === item.origin?.shift)?.label || item.origin?.shift}
                </span>
              )}
              {item.time_hint && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                  <Clock className="w-3 h-3" />
                  {item.time_hint}
                </span>
              )}
              {item.condition && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-100">
                  <MapPin className="w-3 h-3" />
                  {item.condition}
                </span>
              )}
              {item.checked && item.checked_by_name && (
                <span className="text-[11px] text-emerald-600 font-medium">✓ {item.checked_by_name}</span>
              )}
              {canCheck && (
                <button
                  onClick={() => setOpenNotes((p) => ({ ...p, [item.id]: !p[item.id] }))}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200"
                  data-testid={`note-toggle-${item.id}`}
                >
                  <MessageSquarePlus className="w-3 h-3" />
                  Nota
                </button>
              )}
            </div>

            {isCarried && item.note && (
              <div className="mt-2 text-xs bg-amber-100/70 border border-amber-200 rounded-md px-2.5 py-1.5 text-amber-800" data-testid={`carried-note-${item.id}`}>
                <span className="font-semibold">Motivazione turno precedente:</span> {item.note}
              </div>
            )}

            {noteOpen && (
              <input
                type="text"
                defaultValue={item.note}
                placeholder="Aggiungi una nota..."
                disabled={!canCheck}
                onBlur={(e) => { if (e.target.value !== item.note) saveNote(item, e.target.value); }}
                className="mt-2 w-full text-xs border border-stone-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:bg-stone-50"
                data-testid={`note-input-${item.id}`}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="checklist-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-stone-500 -ml-2" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-outfit text-xl font-bold text-stone-900 tracking-tight flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-500" />
                Check List
              </h1>
              <p className="text-xs text-stone-500">Front Office</p>
            </div>
          </div>
          {isSuperAdmin && (
            <Button variant="outline" size="sm" onClick={() => setEditorOpen(true)} className="rounded-full text-xs" data-testid="edit-template-button">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" />
              Modifica
            </Button>
          )}
        </div>
      </header>

      <main className="p-4 pb-28">
        {meta && !meta.has_feature && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="no-feature-notice">
            La Check List non è attiva per questo hotel. Un Super Admin può attivarla dalla Gestione Hotel.
          </div>
        )}

        {/* Shift tabs */}
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="shift-tabs">
          {SHIFTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setShift(s.id)}
              className={`rounded-xl p-2.5 text-center border transition-all ${
                shift === s.id
                  ? "bg-stone-900 text-white border-stone-900 shadow-md"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
              }`}
              data-testid={`shift-tab-${s.id}`}
            >
              <p className="text-sm font-semibold">{s.label}</p>
              <p className={`text-[10px] ${shift === s.id ? "text-white/70" : "text-stone-400"}`}>{s.time}</p>
            </button>
          ))}
        </div>

        {/* Date + status */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
            data-testid="date-picker"
          />
          {instance?.status === "closed" && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-stone-200 text-stone-600" data-testid="closed-badge">
              <Lock className="w-3 h-3" /> Turno chiuso
            </span>
          )}
        </div>

        {/* Progress */}
        {instance && totalCount > 0 && (
          <div className="mb-4" data-testid="progress-block">
            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
              <span>Completamento</span>
              <span className="font-semibold text-stone-700" data-testid="progress-count">{doneCount}/{totalCount}</span>
            </div>
            <div className="h-2 rounded-full bg-stone-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${progress === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-stone-400 text-sm text-center py-12">Caricamento...</p>
        ) : (
          <div className="space-y-5">
            {/* Carried-forward block */}
            {carried.length > 0 && (
              <div data-testid="carried-block">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 mb-2">
                  <ArrowUpFromLine className="w-4 h-4" />
                  Da completare (turno precedente)
                </h2>
                <div className="space-y-2">
                  {carried.map((item) => (
                    <ItemRow key={item.id} item={item} carried />
                  ))}
                </div>
              </div>
            )}

            {/* Sections */}
            {sections.map((section, idx) => (
              <div key={idx} data-testid={`checklist-section-${idx}`}>
                <h2 className="text-sm font-semibold text-stone-800 mb-2 uppercase tracking-wide">{section.title}</h2>
                <div className="space-y-2">
                  {section.items.map((item) => (
                    <ItemRow key={item.id} item={item} />
                  ))}
                </div>
              </div>
            ))}

            {totalCount === 0 && (
              <p className="text-stone-400 text-sm text-center py-12">Nessuna voce configurata per questo turno.</p>
            )}
          </div>
        )}
      </main>

      {/* Close shift bar */}
      {meta?.can_check && instance && instance.status !== "closed" && totalCount > 0 && (
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-40">
          <div className="max-w-3xl mx-auto p-4 bg-gradient-to-t from-stone-50 via-stone-50 to-transparent">
            <Button
              onClick={handleCloseShift}
              className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-xl h-12"
              data-testid="close-shift-button"
            >
              <Lock className="w-4 h-4 mr-2" />
              Chiudi turno e passa le consegne
            </Button>
          </div>
        </div>
      )}

      <ChecklistTemplateEditor open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={loadInstance} />
    </div>
  );
};

export default ChecklistPage;
